import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import FeaturedPromos from './components/FeaturedPromos';
import NavTabs from './components/NavTabs';
import CompetitionsSection from './components/CompetitionsSection';
import BonusSection from './components/BonusSection';
import MobileTopEventsSection from './components/MobileTopEventsSection';
import { MobileTopEventsTableSection } from './components/MobileTopEventsTableSection';
import BottomNavigation from './components/BottomNavigation';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import LivePage from './components/LivePage';
import SportPage from './components/SportPage';
import { TopMatchKombis } from './components/widgets/TopMatchKombis/TopMatchKombis';
import BettingSlipDrawer, { BetItem } from './components/BettingSlipDrawer';
import DesktopHomePage from './components/DesktopHomePage';
import DesktopRegisterPage from './components/DesktopRegisterPage';
import DesktopLoginPage from './components/DesktopLoginPage';
import DesktopMyBetsPage from './components/DesktopMyBetsPage';
import DesktopAccountPage from './components/DesktopAccountPage';
import TodayMatchesPage from './components/TodayMatchesPage';
import MatchDetailPage from './components/MatchDetailPage';
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from './i18n';
import { MobileLiveSection } from './components/MobileLiveSection';
import { MobileLastMinuteSection } from './components/MobileLastMinuteSection';
import { mergeSnapshotWithPatch } from './utils/streamMerge';
import { liveStreamManager } from './services/CatalogStreamManager';
import { useBetSlipOdds } from './hooks/useBetSlipOdds';

const MatchDetailRoute = ({ bets, onAddBet, onRemoveBet, onLoginClick }: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialEvent = location.state?.event;
  const [event, setEvent] = useState(initialEvent);

  React.useEffect(() => {
    if (!initialEvent) {
      // Navigate back if accessed directly without state
      navigate(-1);
    }
  }, [initialEvent, navigate]);

  React.useEffect(() => {
    if (!event || event.scope !== 'live') return;

    const unsubLive = liveStreamManager.subscribe((upserts, deletes) => {
      const msg = upserts.find(u => u.event_id === event.event_id);
      if (msg) {
        setEvent((prev: any) => mergeSnapshotWithPatch(prev, msg));
      } else if (deletes.includes(event.event_id)) {
        // Event deleted, maybe handle it or ignore
      }
    });

    return () => {
      unsubLive();
    };
  }, [event?.event_id]);

  if (!event) {
    return null;
  }
  return (
    <MatchDetailPage
      event={event}
      bets={bets}
      onAddBet={onAddBet}
      onRemoveBet={onRemoveBet}
      onLoginClick={onLoginClick}
      onBack={() => navigate(-1)}
    />
  );
};

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isDesktop;
}

function App() {
  const isDesktop = useIsDesktop();
  const [isLogged, setIsLogged] = useState(() => {
    return localStorage.getItem('access_token') ? true : false;
  });
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  let activePage = 'home';
  if (location.pathname.startsWith('/live')) activePage = 'live';
  else if (location.pathname.startsWith('/account')) activePage = 'account';
  else if (location.pathname.startsWith('/chats')) activePage = 'chats';
  else if (location.pathname.startsWith('/bets')) activePage = 'bets';
  else if (location.pathname.startsWith('/top-events')) activePage = 'top-events';
  else if (location.pathname.startsWith('/todays-matches')) activePage = 'todays-matches';
  else if (location.pathname.startsWith('/match/')) activePage = 'match';
  else if (location.pathname.startsWith('/german-football')) activePage = 'german-football';
  else if (location.pathname.startsWith('/european-football')) activePage = 'european-football';
  else if (location.pathname.startsWith('/competition')) activePage = 'competition';
  else if (location.pathname.startsWith('/sport')) activePage = 'sport';
  let headerTitle = "Mein Account";
  if (activePage === 'account') {
    const accountTab = location.pathname.split('/')[2] || '';
    if (accountTab === 'meine-daten') headerTitle = t('account.meineDaten') || 'Meine Daten';
    else if (accountTab === 'kontoverifizierung') headerTitle = t('account.kontoverifizierung') || 'Kontoverifizierung';
    else if (accountTab === 'kontoverwaltung') headerTitle = t('account.kontoverwaltung') || 'Kontoverwaltung';
    else if (accountTab === 'transaktionsverlauf') headerTitle = t('account.transaktionsverlauf') || 'Transaktionsverlauf';
    else if (accountTab === 'guthaben') headerTitle = t('account.guthaben') || 'Guthaben';
    else if (accountTab === 'auszahlung') headerTitle = t('account.auszahlung') || 'Auszahlung';
    else if (accountTab === 'einstellungen') headerTitle = t('account.einstellungen') || 'Einstellungen';
    else if (accountTab === 'neuigkeiten') headerTitle = t('account.neuigkeiten') || 'Neuigkeiten';
    else if (accountTab === 'apps') headerTitle = t('account.apps') || 'Apps';
    else if (accountTab === 'kontakt') headerTitle = t('account.kontakt') || 'Kontakt';
    else headerTitle = t('account.myAccount') || 'Mein Account';
  }
  const [bets, setBets] = useState<BetItem[]>([]);
  useBetSlipOdds(bets, setBets);
  const [showSlip, setShowSlip] = useState(false);
  const [selectedMobileCompetition, setSelectedMobileCompetition] = useState<string | null>(null);
  const [selectedMobileSport, setSelectedMobileSport] = useState<string | null>(null);

  const handleSelectMobileCompetition = (categoryName: string, tournamentName: string) => {
    setSelectedMobileCompetition(tournamentName);
    setSelectedMobileSport('Soccer');
    navigate(`/sport/Soccer/${encodeURIComponent(categoryName)}/${encodeURIComponent(tournamentName)}`);
  };

  const handleNavigate = (id: string) => {
    setShowLogin(false);
    setShowRegister(false);
    if (id === 'slip') {
      setShowSlip(true);
    } else {
      navigate('/' + id);
      if (id === 'home') setActiveTab('');
    }
  };

  const handleTabChange = (tabId: string) => {
    setShowLogin(false);
    setShowRegister(false);
    if (tabId === 'football') {
      setSelectedMobileSport('Soccer');
      navigate('/sport/Soccer');
    } else if (tabId === 'basketball') {
      setSelectedMobileSport('Basketball');
      navigate('/sport/Basketball');
    } else if (tabId === 'tennis') {
      setSelectedMobileSport('Tennis');
      navigate('/sport/Tennis');
    } else if (tabId === 'live') {
      navigate('/live');
    } else if (tabId === 'today') {
      navigate('/todays-matches');
    } else {
      setActiveTab(tabId);
    }
  };

  const handleLoginSuccess = () => {
    setIsLogged(true);
    setShowLogin(false);
    window.dispatchEvent(new Event('auth-changed'));
  };

  const handleAddBet = (bet: BetItem) => setBets((prev) => [...prev, bet]);
  const handleRemoveBet = (id: string) => setBets((prev) => prev.filter((b) => b.id !== id));
  const handleClearAll = () => setBets([]);

  // Desktop layout
  if (isDesktop) {
    return (
      <>
        {showRegister && (
          <DesktopRegisterPage
            onClose={() => setShowRegister(false)}
            onLoginClick={() => { setShowRegister(false); setShowLogin(true); }}
          />
        )}
        {showLogin && (
          <DesktopLoginPage
            onClose={() => setShowLogin(false)}
            onRegisterClick={() => { setShowLogin(false); setShowRegister(true); }}
            onLoginSuccess={handleLoginSuccess}
          />
        )}
        <DesktopHomePage
          bets={bets}
          onAddBet={handleAddBet}
          onRemoveBet={handleRemoveBet}
          onClearAll={handleClearAll}
          onLoginClick={() => setShowLogin(true)}
          onRegisterClick={() => setShowRegister(true)}
          activePage={activePage}
          onNavigate={handleNavigate}
        />
      </>
    );
  }

  // Mobile layout
  return (
    <div className="min-h-screen bg-tipico-gray-light w-full max-w-full overflow-x-hidden font-inter">
      {showRegister && (
        <RegisterPage
          onClose={() => setShowRegister(false)}
          onLoginClick={() => { setShowRegister(false); setShowLogin(true); }}
        />
      )}
      {showLogin && (
        <LoginPage
          onClose={() => setShowLogin(false)}
          onRegisterClick={() => { setShowLogin(false); setShowRegister(true); }}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {showSlip && (
        <BettingSlipDrawer
          bets={bets}
          onRemoveBet={handleRemoveBet}
          onClearAll={handleClearAll}
          onClose={() => setShowSlip(false)}
          onLoginClick={() => { setShowSlip(false); setShowLogin(true); }}
        />
      )}

      {activePage === 'sport' ? (
        <>
          <Routes>
            <Route path="/sport/:sportName/:categoryName/:tournamentName" element={
              <SportPage
                isLogged={isLogged}
                onLoginClick={() => setShowLogin(true)}
                bets={bets}
                onAddBet={handleAddBet}
                onRemoveBet={handleRemoveBet}
                initialCompetitionName={selectedMobileCompetition}
                onClearInitialCompetition={() => setSelectedMobileCompetition(null)}
                initialSportName={selectedMobileSport}
                onClearInitialSport={() => setSelectedMobileSport(null)}
              />
            } />
            <Route path="/sport/:sportName/:categoryName" element={
              <SportPage
                isLogged={isLogged}
                onLoginClick={() => setShowLogin(true)}
                bets={bets}
                onAddBet={handleAddBet}
                onRemoveBet={handleRemoveBet}
                initialCompetitionName={selectedMobileCompetition}
                onClearInitialCompetition={() => setSelectedMobileCompetition(null)}
                initialSportName={selectedMobileSport}
                onClearInitialSport={() => setSelectedMobileSport(null)}
              />
            } />
            <Route path="/sport/:sportName" element={
              <SportPage
                isLogged={isLogged}
                onLoginClick={() => setShowLogin(true)}
                bets={bets}
                onAddBet={handleAddBet}
                onRemoveBet={handleRemoveBet}
                initialCompetitionName={selectedMobileCompetition}
                onClearInitialCompetition={() => setSelectedMobileCompetition(null)}
                initialSportName={selectedMobileSport}
                onClearInitialSport={() => setSelectedMobileSport(null)}
              />
            } />
            <Route path="/sport" element={
              <SportPage
                isLogged={isLogged}
                onLoginClick={() => setShowLogin(true)}
                bets={bets}
                onAddBet={handleAddBet}
                onRemoveBet={handleRemoveBet}
                initialCompetitionName={selectedMobileCompetition}
                onClearInitialCompetition={() => setSelectedMobileCompetition(null)}
                initialSportName={selectedMobileSport}
                onClearInitialSport={() => setSelectedMobileSport(null)}
              />
            } />
          </Routes>
          <BottomNavigation active={activePage} onNavigate={handleNavigate} betCount={bets.length} />
        </>
      ) : activePage === 'live' ? (
        <>
          <div className="fixed top-0 left-0 right-0 z-[110] h-[56px]">
            <Header isLogged={isLogged} onLoginClick={() => setShowLogin(true)} onNavigate={handleNavigate} activePage={activePage} onBack={() => navigate('/')} title="Live Wetten" />
          </div>
          <main className="pb-20 bg-tipico-gray-light mt-[56px] relative z-[90]" style={{ borderRadius: '12px 12px 0px 0px' }}>
            <LivePage bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} onLoginClick={() => setShowLogin(true)} />
          </main>
          <BottomNavigation active={activePage} onNavigate={handleNavigate} betCount={bets.length} />
        </>
      ) : activePage === 'todays-matches' ? (
        <>
          <div className="fixed top-0 left-0 right-0 z-[110] h-[56px]">
            <Header isLogged={isLogged} onLoginClick={() => setShowLogin(true)} onNavigate={handleNavigate} activePage={activePage} onBack={() => navigate('/')} title="Heute Wetten" />
          </div>
          <main className="pb-20 bg-tipico-gray-light mt-[56px] relative z-[90]" style={{ borderRadius: '12px 12px 0px 0px' }}>
            <div className="px-3">
              <TodayMatchesPage bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} onLoginClick={() => navigate('/login')} />
            </div>
          </main>
          <BottomNavigation active={activePage} onNavigate={handleNavigate} betCount={bets.length} />
        </>
      ) : activePage === 'match' ? (
        <>
          <MatchDetailRoute bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} onLoginClick={() => navigate('/login')} />
          <BottomNavigation active={activePage} onNavigate={handleNavigate} betCount={bets.length} />
        </>
      ) : activePage === 'bets' ? (
        <>
          <div className="fixed top-0 left-0 right-0 z-[110] h-[56px]">
            <Header isLogged={isLogged} onLoginClick={() => navigate('/login')} onRegisterClick={() => navigate('/register')} onNavigate={handleNavigate} activePage={activePage} />
          </div>
          <main className="pb-20 px-3 pt-3 bg-tipico-gray-light mt-[56px] relative z-[90]" style={{ borderRadius: '12px 12px 0px 0px' }}>
            <DesktopMyBetsPage onLoginClick={() => navigate('/login')} />
          </main>
          <BottomNavigation active={activePage} onNavigate={handleNavigate} betCount={bets.length} />
        </>
      ) : activePage === 'account' ? (
        <>
          <div className="fixed top-0 left-0 right-0 z-[110] h-[56px]">
            <Header
              isLogged={isLogged}
              onLoginClick={() => navigate('/login')}
              onRegisterClick={() => navigate('/register')}
              onNavigate={handleNavigate}
              activePage={activePage}
              onBack={() => {
                if (location.pathname === '/account' || location.pathname === '/account/') {
                  navigate('/');
                } else {
                  navigate('/account');
                }
              }}
            />
          </div>
          <main className="pb-20 px-3 pt-3 bg-tipico-gray-light mt-[56px] relative z-[90]" style={{ borderRadius: '12px 12px 0px 0px' }}>
            <DesktopAccountPage onLoginClick={() => setShowLogin(true)} />
          </main>
          <BottomNavigation active={activePage} onNavigate={handleNavigate} betCount={bets.length} />
        </>
      ) : (
        <>
          <div className="fixed top-0 left-0 right-0 z-[110] h-[56px]">
            <Header
              isLogged={isLogged}
              onLoginClick={() => setShowLogin(true)}
              onRegisterClick={() => setShowRegister(true)}
              onNavigate={handleNavigate}
              activePage={activePage}
              onBack={activeTab ? () => setActiveTab('') : undefined}
              title={undefined}
            />
          </div>
          {!activeTab && (
            <div className="mt-[56px] shadow-sm relative z-50" style={{ background: 'linear-gradient(rgb(156 9 41) 0%, rgb(57, 7, 18) 100%)' }}>
              <NavTabs activeTab={activeTab} onTabChange={handleTabChange} />
            </div>
          )}
          <main className={`pb-20 bg-tipico-gray-light relative z-[90] ${activeTab ? 'mt-[56px]' : '-mt-2'}`} style={{ borderRadius: '12px 12px 0px 0px' }}>
            {activeTab === 'live' ? (
              <LivePage bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} onLoginClick={() => setShowLogin(true)} />
            ) : activeTab === 'today' ? (
              <div className="px-3">
                <TodayMatchesPage bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} onLoginClick={() => setShowLogin(true)} />
              </div>
            ) : (
              <>
                <MobileTopEventsSection bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} />
                <TopMatchKombis bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} />
                <CompetitionsSection onSelectCompetition={handleSelectMobileCompetition} />
                <MobileTopEventsTableSection bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} />
                <MobileLastMinuteSection bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} />
                <MobileLiveSection bets={bets} onAddBet={handleAddBet} onRemoveBet={handleRemoveBet} />
              </>
            )}
            {/* default home / heute view shows promo content with no tab selected */}
          </main>
          <BottomNavigation active={activePage} onNavigate={handleNavigate} betCount={bets.length} />
        </>
      )}
    </div>
  );
}
export default App;
